import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Trophy, CheckCircle, XCircle, ArrowLeft, Rocket, Star, TrendingUp, Target } from 'lucide-react';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';

export default function Results() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [isFreeTest, setIsFreeTest] = useState(false);

  const fetchResult = useCallback(async () => {
    try {
      const response = await api.get(`/results/${id}`);
      setResult(response.data);
      
      // Check if this was a free test
      const testResponse = await api.get(`/test-series/${response.data.test_series_id}`);
      const isFree = testResponse.data.is_free || false;
      setIsFreeTest(isFree);
      
      // Fetch questions with answers for detailed review
      const questionsResponse = await api.get(`/results/${id}/questions`);
      setQuestions(questionsResponse.data);
      
      // Show conversion modal for free tests after a delay
      if (isFree) {
        setTimeout(() => setShowConversionModal(true), 2000);
      }
    } catch (error) {
      toast.error('Failed to load results');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchResult();
  }, [id, user, navigate, fetchResult]);

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-success';
    if (score >= 50) return 'text-warning';
    return 'text-destructive';
  };

  const getPerformanceMessage = (score) => {
    if (score >= 90) return 'Outstanding Performance! 🎉';
    if (score >= 75) return 'Great Job! Keep it up! 👏';
    if (score >= 50) return 'Good Effort! Room for improvement';
    return 'Keep Practicing! You can do better';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading results...</p>
        </div>
      </div>
    );
  }

  const accuracy = ((result.correct_count / result.total_questions) * 100).toFixed(1);

  const getUserAnswer = (questionId) => {
    return result.answers[questionId] || 'Not Attempted';
  };

  const isCorrect = (question) => {
    const userAnswer = getUserAnswer(question.id);
    if (userAnswer === 'Not Attempted') return false;
    return userAnswer.toLowerCase().trim() === question.correct_answer.toLowerCase().trim();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="results-page">
      {/* Navbar */}
      <nav className="border-b border-border backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b913f950-9aae-49dc-aba5-d378a13b23f2/images/cfb7f6d255e29e51a76f73861f035a7bbb40bada25de0eb5061520d6d7d4dab1.png" 
            alt="Pinaka Logo"
            className="h-10 w-auto cursor-pointer"
            onClick={() => navigate('/')}
          />
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button onClick={() => navigate('/dashboard')} data-testid="nav-dashboard-btn">
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')} 
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-primary/10 mb-6">
            <Trophy className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-4xl font-outfit font-bold mb-4">Test Completed!</h1>
          <p className="text-xl text-muted-foreground">{result.test_series_title}</p>
        </motion.div>

        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-lg p-8 md:p-12 border border-border mb-8"
        >
          <div className="text-center mb-8">
            <div className={`text-7xl font-mono font-bold ${getScoreColor(result.score)} mb-4`} data-testid="score-display">
              {result.score}%
            </div>
            <p className="text-2xl font-outfit font-semibold">{getPerformanceMessage(result.score)}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <CheckCircle className="w-10 h-10 text-success mx-auto mb-3" />
              <div className="text-3xl font-mono font-bold text-success mb-1" data-testid="correct-count">
                {result.correct_count}
              </div>
              <div className="text-muted-foreground">Correct Answers</div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <XCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
              <div className="text-3xl font-mono font-bold text-destructive mb-1" data-testid="incorrect-count">
                {result.incorrect_count}
              </div>
              <div className="text-muted-foreground">Incorrect Answers</div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Trophy className="w-10 h-10 text-accent mx-auto mb-3" />
              <div className="text-3xl font-mono font-bold text-accent mb-1" data-testid="accuracy">
                {accuracy}%
              </div>
              <div className="text-muted-foreground">Accuracy</div>
            </div>
          </div>
        </motion.div>

        {/* Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card border border-border rounded-lg p-6 mb-8"
        >
          <h3 className="text-xl font-outfit font-semibold mb-4">Test Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">Total Questions</span>
              <span className="font-semibold font-mono">{result.total_questions}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">Correct Answers</span>
              <span className="font-semibold font-mono text-success">{result.correct_count}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">Incorrect Answers</span>
              <span className="font-semibold font-mono text-destructive">{result.incorrect_count}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">Score</span>
              <span className={`font-semibold font-mono text-2xl ${getScoreColor(result.score)}`}>{result.score}%</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">Submitted At</span>
              <span className="font-semibold">{new Date(result.submitted_at).toLocaleString()}</span>
            </div>
          </div>
        </motion.div>

        {/* Detailed Question Review */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8"
        >
          <h3 className="text-2xl font-outfit font-bold mb-6">Question-by-Question Review</h3>
          <div className="space-y-4">
            {questions.map((question, index) => {
              const userAnswer = getUserAnswer(question.id);
              const correct = isCorrect(question);
              const notAttempted = userAnswer === 'Not Attempted';

              return (
                <div
                  key={question.id}
                  className={`bg-card border rounded-lg p-6 ${
                    correct ? 'border-success/30 bg-success/5' : 
                    notAttempted ? 'border-border' : 
                    'border-destructive/30 bg-destructive/5'
                  }`}
                  data-testid={`review-question-${index}`}
                >
                  {/* Question Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold font-mono text-primary">Q{index + 1}</span>
                      <span className="text-xs px-2 py-1 bg-accent/10 text-accent rounded-full font-semibold">
                        {question.question_type === 'mcq' ? 'MCQ' : 'Numerical'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {correct ? (
                        <span className="flex items-center gap-1 text-success font-semibold">
                          <CheckCircle className="w-5 h-5" />
                          Correct
                        </span>
                      ) : notAttempted ? (
                        <span className="flex items-center gap-1 text-muted-foreground font-semibold">
                          <XCircle className="w-5 h-5" />
                          Not Attempted
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-destructive font-semibold">
                          <XCircle className="w-5 h-5" />
                          Incorrect
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Question Text */}
                  <p className="text-lg font-manrope mb-4">{question.question_text}</p>

                  {/* Options for MCQ */}
                  {question.question_type === 'mcq' && question.options && (
                    <div className="space-y-2 mb-4">
                      {question.options.map((option, idx) => {
                        const isUserAnswer = userAnswer === option;
                        const isCorrectOption = option === question.correct_answer;

                        return (
                          <div
                            key={idx}
                            className={`p-3 rounded-md border text-sm ${
                              isCorrectOption
                                ? 'bg-success/10 border-success/30 text-success font-semibold'
                                : isUserAnswer && !correct
                                ? 'bg-destructive/10 border-destructive/30 text-destructive'
                                : 'bg-muted/30 border-border text-muted-foreground'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-bold">{String.fromCharCode(65 + idx)}.</span>
                              <span>{option}</span>
                              {isCorrectOption && <CheckCircle className="w-4 h-4 ml-auto" />}
                              {isUserAnswer && !correct && <XCircle className="w-4 h-4 ml-auto" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Answer Summary */}
                  <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div>
                      <span className="text-sm text-muted-foreground">Your Answer:</span>
                      <div className={`font-semibold ${
                        correct ? 'text-success' : 
                        notAttempted ? 'text-muted-foreground' : 
                        'text-destructive'
                      }`}>
                        {userAnswer}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Correct Answer:</span>
                      <div className="font-semibold text-success">{question.correct_answer}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex gap-4 justify-center"
        >
          <Button 
            onClick={() => navigate(`/test/${result.test_series_id}`)}
            className="bg-primary hover:bg-primary/90 text-white font-semibold"
            data-testid="retake-test-btn"
          >
            Retake Test
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/test-series')}
            data-testid="browse-more-btn"
          >
            Browse More Tests
          </Button>
        </motion.div>
      </div>

      {/* Conversion Modal for Free Test */}
      <Dialog open={showConversionModal} onOpenChange={setShowConversionModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-card to-primary/5 border-2 border-primary/20">
          <DialogHeader>
            <DialogTitle className="text-2xl md:text-3xl font-outfit font-bold text-center mb-2">
              🎉 Great Job on Your Free Test!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 md:space-y-6 px-2">
            <div className="text-center">
              <p className="text-base md:text-xl text-muted-foreground mb-4">
                You've experienced just a <span className="text-primary font-bold">tiny fraction</span> of what we offer
              </p>
              <div className="inline-block p-4 md:p-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border-2 border-primary/20">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-2">
                  1000+
                </div>
                <div className="text-xs md:text-sm text-muted-foreground">More tests waiting for you</div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-4 md:p-6 border-2 border-border">
              <h3 className="font-outfit font-bold text-lg md:text-xl mb-3 md:mb-4 flex items-center gap-2">
                <Rocket className="w-5 h-5 md:w-6 md:h-6 text-primary flex-shrink-0" />
                <span>Unlock Your Full Potential</span>
              </h3>
              <div className="space-y-2 md:space-y-3">
                <div className="flex items-start gap-2 md:gap-3">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-success flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm md:text-base">1000+ Practice Tests</div>
                    <div className="text-xs md:text-sm text-muted-foreground">JEE Main & Advanced pattern questions</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-success flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm md:text-base">Detailed Performance Analytics</div>
                    <div className="text-xs md:text-sm text-muted-foreground">Track progress across all subjects</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-success flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm md:text-base">All India Rank Comparison</div>
                    <div className="text-xs md:text-sm text-muted-foreground">See where you stand nationwide</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-success flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm md:text-base">Expert Solutions & Tips</div>
                    <div className="text-xs md:text-sm text-muted-foreground">From IIT faculty members</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-xl p-4 md:p-6 border-2 border-orange-500/30">
              <div className="flex items-center justify-between gap-3 mb-2 md:mb-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs md:text-sm text-muted-foreground">Limited Time Offer</div>
                  <div className="text-xl md:text-2xl font-bold">Get 50% OFF</div>
                </div>
                <div className="text-3xl md:text-4xl flex-shrink-0">🎯</div>
              </div>
              <p className="text-xs md:text-sm text-muted-foreground">
                Join now and get access to all premium test series at half price!
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                className="flex-1 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold text-base md:text-lg py-5 md:py-6"
                onClick={() => navigate('/test-series')}
                data-testid="view-premium-tests-btn"
              >
                <Star className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                <span className="truncate">View Premium Tests</span>
              </Button>
              <Button
                variant="outline"
                className="sm:w-auto px-6 py-5 md:py-6"
                onClick={() => setShowConversionModal(false)}
                data-testid="maybe-later-btn"
              >
                Maybe Later
              </Button>
            </div>

            <div className="text-center pb-2">
              <p className="text-xs md:text-sm text-muted-foreground">
                <span className="font-semibold text-primary">50,000+ students</span> are already improving with our platform
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
